import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-900 mb-6 hover:text-gray-700"
        >
          <ChevronLeft size={20} />
          <span className="ml-1">Back</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-6 text-sm text-gray-700">
          {/* Introduction */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Introduction</h2>
            <p className="leading-relaxed">
              Hey Sri Lanka ("we," "our," or "us") respects your privacy and is committed to protecting 
              your personal data. This Privacy Policy explains how we collect, use, and safeguard your 
              information when you use our mobile application and services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Information We Collect</h2>
            
            <h3 className="font-medium text-gray-800 mt-3 mb-1">Account Information</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Email address (for account creation and communication)</li>
              <li>Name and profile photo (from Google Sign-In, if used)</li>
              <li>Phone number (optional, for WhatsApp/Telegram verification)</li>
            </ul>

            <h3 className="font-medium text-gray-800 mt-3 mb-1">Usage Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Chat conversations with our AI assistant</li>
              <li>Saved places and trip itineraries</li>
              <li>Fare calculation history</li>
              <li>App preferences and settings</li>
            </ul>

            <h3 className="font-medium text-gray-800 mt-3 mb-1">Location Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Location coordinates when using map features (with your permission)</li>
              <li>Pickup and destination points for fare calculations</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">3. How We Use Your Information</h2>
            <p className="leading-relaxed mb-2">We use your information to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Provide and improve our travel assistance services</li>
              <li>Personalize your experience and recommendations</li>
              <li>Process quote requests and connect you with service providers</li>
              <li>Send important updates about your trips or account</li>
              <li>Respond to your support inquiries</li>
              <li>Analyze usage patterns to improve our AI and features</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Data Sharing</h2>
            <p className="leading-relaxed mb-2">We may share your information with:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Service Providers:</strong> When you request quotes, we share relevant trip details with travel service providers</li>
              <li><strong>Third-Party Services:</strong> We use Google Maps, Firebase, and other services that may process your data according to their privacy policies</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="leading-relaxed mt-2">
              We do not sell your personal data to third parties.
            </p>
          </section>

          {/* Data Storage */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Data Storage & Security</h2>
            <p className="leading-relaxed">
              Your data is stored securely using Google Firebase infrastructure. We implement 
              industry-standard security measures including encryption, secure connections, and 
              access controls. However, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Data Retention</h2>
            <p className="leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide 
              services. Chat history is automatically limited to prevent excessive storage. You can 
              request deletion of your account and associated data at any time.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">7. Your Rights</h2>
            <p className="leading-relaxed mb-2">You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and data</li>
              <li>Export your saved places and trips</li>
              <li>Opt out of marketing communications</li>
              <li>Clear your chat history at any time</li>
            </ul>
          </section>

          {/* Cookies & Local Storage */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Cookies & Local Storage</h2>
            <p className="leading-relaxed">
              We use local storage to save your preferences, authentication state, and improve 
              performance. This data stays on your device and is not transmitted to our servers 
              unless necessary for service functionality.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Children's Privacy</h2>
            <p className="leading-relaxed">
              Our services are not directed to children under 13. We do not knowingly collect 
              personal information from children. If you believe we have collected data from a 
              child, please contact us immediately.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of significant 
              changes through the app or via email. Your continued use after changes constitutes 
              acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions about this Privacy Policy or your data, please contact us at{' '}
              <a href="mailto:malintha.levine@gmail.com" className="text-orange-600 hover:underline">
                malintha.levine@gmail.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Hey Sri Lanka. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

